import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';

interface Production {
  id: string;
  title: string;
  description?: string;
  productionDate: string;
  location?: string;
  status: string;
  complianceStatus: string;
}

interface Participant {
  id: string;
  role?: string;
  rightsPercentage?: number;
  model: {
    id: string;
    legalName: string;
    stageNames: string[];
    verificationStatus: string;
  };
}

interface Model {
  id: string;
  legalName: string;
  stageNames?: string[];
  email?: string;
}

export default function ProductionDetail() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  // Modal state
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [modelSearch, setModelSearch] = useState('');
  const [selectedModelId, setSelectedModelId] = useState('');
  const [participantRole, setParticipantRole] = useState('');
  const [rightsPercentage, setRightsPercentage] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ['production', id],
    queryFn: () =>
      api.get<{ production: Production; participants: Participant[] }>(
        `/productions/${id}`
      ),
  });

  // Search for models
  const { data: searchResults } = useQuery({
    queryKey: ['models-search', modelSearch],
    queryFn: () => api.get<{ models: Model[] }>(`/models/search?q=${encodeURIComponent(modelSearch)}`),
    enabled: modelSearch.length >= 2,
  });

  // Add participant mutation
  const addParticipantMutation = useMutation({
    mutationFn: (data: { modelProfileId: string; role?: string; rightsPercentage?: number }) =>
      api.post(`/productions/${id}/participants`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production', id] });
      setShowAddParticipant(false);
      setModelSearch('');
      setSelectedModelId('');
      setParticipantRole('');
      setRightsPercentage(0);
    },
  });

  // Remove participant mutation
  const removeParticipantMutation = useMutation({
    mutationFn: (participantId: string) =>
      api.delete(`/productions/${id}/participants/${participantId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production', id] });
    },
  });

  const handleAddParticipant = () => {
    if (!selectedModelId) return;
    addParticipantMutation.mutate({
      modelProfileId: selectedModelId,
      role: participantRole || undefined,
      rightsPercentage: rightsPercentage || undefined,
    });
  };

  if (isLoading) {
    return <div className="loading">Loading production...</div>;
  }

  const production = data?.data.production;
  const participants = data?.data.participants || [];
  const models = searchResults?.data.models || [];

  if (!production) {
    return <div className="error">Production not found</div>;
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>{production.title}</h1>
          <p className="page-subtitle">{production.productionDate}</p>
        </div>
        <div className="header-actions">
          <Link to={`/documents/new?productionId=${id}`} className="btn btn-primary">
            Create Document
          </Link>
        </div>
      </div>

      <div className="detail-grid">
        <div className="card">
          <h3>Production Details</h3>
          <dl className="detail-list">
            <dt>Title</dt>
            <dd>{production.title}</dd>

            <dt>Date</dt>
            <dd>{production.productionDate}</dd>

            <dt>Location</dt>
            <dd>{production.location || '-'}</dd>

            <dt>Description</dt>
            <dd>{production.description || '-'}</dd>
          </dl>
        </div>

        <div className="card">
          <h3>Status</h3>
          <div className="status-badges">
            <div>
              <label>Production Status</label>
              <span className={`badge badge-${production.status}`}>
                {production.status}
              </span>
            </div>
            <div>
              <label>Compliance</label>
              <span
                className={`badge ${production.complianceStatus === 'complete' ? 'badge-success' : 'badge-warning'}`}
              >
                {production.complianceStatus}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Participants</h3>
          <button
            className="btn btn-secondary"
            onClick={() => setShowAddParticipant(true)}
          >
            Add Participant
          </button>
        </div>

        {participants.length === 0 ? (
          <p className="empty-text">No participants added yet</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Stage Names</th>
                <th>Role</th>
                <th>Rights %</th>
                <th>Verified</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {participants.map((participant) => (
                <tr key={participant.id}>
                  <td>
                    <Link
                      to={`/models/${participant.model.id}`}
                      className="link"
                    >
                      {participant.model.legalName}
                    </Link>
                  </td>
                  <td>
                    {Array.isArray(participant.model.stageNames)
                      ? participant.model.stageNames.join(', ')
                      : participant.model.stageNames || '-'}
                  </td>
                  <td>{participant.role || '-'}</td>
                  <td>
                    {participant.rightsPercentage !== undefined
                      ? `${participant.rightsPercentage}%`
                      : '-'}
                  </td>
                  <td>
                    <span
                      className={`badge ${participant.model.verificationStatus === 'verified' ? 'badge-success' : 'badge-warning'}`}
                    >
                      {participant.model.verificationStatus}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn btn-small btn-danger"
                      onClick={() => {
                        if (window.confirm(`Remove ${participant.model.legalName} from this production?`)) {
                          removeParticipantMutation.mutate(participant.id);
                        }
                      }}
                      disabled={removeParticipantMutation.isPending}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Participant Modal */}
      {showAddParticipant && (
        <div className="modal-overlay" onClick={() => setShowAddParticipant(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Participant</h2>
              <button
                className="modal-close"
                onClick={() => setShowAddParticipant(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="model-search">Search for Model</label>
                <input
                  type="text"
                  id="model-search"
                  value={modelSearch}
                  onChange={(e) => setModelSearch(e.target.value)}
                  placeholder="Search by name or email..."
                />
              </div>

              {models.length > 0 && (
                <div className="form-group">
                  <label>Select Model</label>
                  <div className="model-list">
                    {models.map((model) => (
                      <label key={model.id} className="model-option">
                        <input
                          type="radio"
                          name="selectedModel"
                          value={model.id}
                          checked={selectedModelId === model.id}
                          onChange={(e) => setSelectedModelId(e.target.value)}
                        />
                        <span>
                          {model.legalName}
                          {model.stageNames?.length ? ` (${model.stageNames[0]})` : ''}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {selectedModelId && (
                <>
                  <div className="form-group">
                    <label htmlFor="participant-role">Role (Optional)</label>
                    <input
                      type="text"
                      id="participant-role"
                      value={participantRole}
                      onChange={(e) => setParticipantRole(e.target.value)}
                      placeholder="e.g., Performer, Model, Witness"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="rights-percentage">Rights Percentage (Optional)</label>
                    <input
                      type="number"
                      id="rights-percentage"
                      min="0"
                      max="100"
                      value={rightsPercentage}
                      onChange={(e) => setRightsPercentage(parseInt(e.target.value) || 0)}
                      placeholder="0"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddParticipant(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddParticipant}
                disabled={!selectedModelId || addParticipantMutation.isPending}
              >
                {addParticipantMutation.isPending ? 'Adding...' : 'Add Participant'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #6b7280;
          line-height: 1;
        }

        .modal-body {
          padding: 20px;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 20px;
          border-top: 1px solid #e5e7eb;
        }

        .model-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          max-height: 200px;
          overflow-y: auto;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 8px;
        }

        .model-option {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px;
          border-radius: 4px;
          cursor: pointer;
        }

        .model-option:hover {
          background: #f3f4f6;
        }
      `}</style>
    </div>
  );
}
